/* 
 * Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
 * Distributed under the TruVideo proprietary license. 
 */

/**
 * Per-identifier-type format validation rules for OCR captures and manual entry.
 *
 * Each rule exposes:
 *   validate(text: string): boolean  — true if the value is acceptable
 *   message: string                  — user-facing error shown when validate() returns false
 *   hint: string                     — short format hint shown in the input placeholder / tooltip
 */
const IDENTIFIER_RULES = {
  vin: {
    validate: (text) => /^[A-HJ-NPR-Z0-9]{17}$/.test(text.trim().toUpperCase()),
    message: 'Please enter a valid 17-character VIN. VINs cannot contain the letters I, O, or Q.',
    hint: 'Example: 1HGBH41JXMN109186',
  },

  odometer: {
    validate: (text) => /^\d{1,6}$/.test(text.trim()),
    message: 'Please enter a valid odometer reading',
    hint: 'Example: 45230',
  },

  other: {
    validate: () => true,
    message: '',
    hint: '',
  },
};

/**
 * Validates an identifier value against the rules for its format type.
 *
 * @param {string} format - The identifier_format value (e.g. 'vin', 'odometer').
 * @param {string} text   - The captured or manually-entered text to validate.
 * @returns {{ valid: boolean, message: string | null }}
 *   `valid`   — true if the value passes format validation (or is empty — presence is a separate concern).
 *   `message` — the user-facing error string, or null if valid.
 */
export const validateIdentifier = (format, text) => {
  // Empty values are always treated as "not yet filled" — presence validation
  // is handled at submit time, not here.
  if (!text || !text.trim()) {
    return { valid: true, message: null };
  }

  const rule = IDENTIFIER_RULES[format] || IDENTIFIER_RULES.other;
  const valid = rule.validate(text);

  return {
    valid,
    message: valid ? null : rule.message,
  };
};

/**
 * Returns the hint string for a given identifier format, used in input placeholders.
 *
 * @param {string} format
 * @returns {string}
 */
export const getIdentifierHint = (format) => {
  return IDENTIFIER_RULES[format]?.hint || '';
};
